<template>
  <div class="show_all_content_wrapper user_subscription_history">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TITLES.user_subscription_history") }}</h5>
        </div>
        <div class="col-12 text-end mb-3">
          <v-btn @click="$router.go(-1)" style="color: #f7941e">
            <i class="fas fa-backward"></i>
          </v-btn>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!-- Start:: Loading -->
      <div v-if="loading" class="text-center py-5">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      <!-- End:: Loading -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        v-else
        class="thumb"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        hide-default-footer
        :items-per-page="-1"
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.serial`]="{ index }">
          <span>{{ index + 1 }}</span>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Subscription Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="getStatusColor(item.status)" text-color="white" small>
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>
        <!-- End:: Subscription Status -->

        <!-- Start:: Price -->
        <template v-slot:[`item.price`]="{ item }">
          <span>{{ item.price }} {{ $t("PLACEHOLDERS.sar") }}</span>
        </template>
        <!-- End:: Price -->

        <!-- Start:: Tax -->
        <template v-slot:[`item.tax_in_value`]="{ item }">
          <span>{{ item.tax_in_value }} {{ $t("PLACEHOLDERS.sar") }}</span>
        </template>
        <!-- End:: Tax -->

        <!-- Start:: Start Date -->
        <template v-slot:[`item.start_date`]="{ item }">
          <span>{{ formatDate(item.start_date) }}</span>
        </template>
        <!-- End:: Start Date -->

        <!-- Start:: End Date -->
        <template v-slot:[`item.end_date`]="{ item }">
          <span>{{ formatDate(item.end_date) }}</span>
        </template>
        <!-- End:: End Date -->

        <!-- Start:: Type -->
        <template v-slot:[`item.type`]="{ item }">
          <span>{{ item.type || $t("PLACEHOLDERS.monthly") }}</span>
        </template>
        <!-- End:: Type -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->
  </div>
</template>

<script>
export default {
  name: "UserSubscriptionHistory",

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      // End:: Loading Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("PLACEHOLDERS.serial_number"),
          value: "serial",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_id"),
          value: "id",
          align: "center",
          width: "100",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_type"),
          value: "type",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_start_date"),
          value: "start_date",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_end_date"),
          value: "end_date",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_price"),
          value: "price",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.tax_value"),
          value: "tax_in_value",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.deactivation_reason"),
          value: "deactivation_reason",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.total_extensions_days"),
          value: "total_extensions_days",
          align: "center",
          sortable: false,
        },
      ],
      tableRows: [],
      // End:: Table Data
    };
  },

  methods: {
    // Start:: Get Status Color
    getStatusColor(status) {
      switch (status) {
        case "active":
          return "success";
        case "expired":
          return "error";
        case "pending_renewal":
          return "warning";
        case "cancelled":
          return "grey";
        default:
          return "primary";
      }
    },
    // End:: Get Status Color

    // Start:: Get Status Text
    getStatusText(status) {
      switch (status) {
        case "active":
          return this.$t("PLACEHOLDERS.active_subscription");
        case "expired":
          return this.$t("PLACEHOLDERS.expired_subscription");
        case "pending_renewal":
          return this.$t("PLACEHOLDERS.pending_renewal");
        case "cancelled":
          return this.$t("PLACEHOLDERS.cancelled_subscription");
        default:
          return status;
      }
    },
    // End:: Get Status Text

    // Start:: Format Date
    formatDate(dateString) {
      if (!dateString) return "-";
      return dateString.split(" ")[0].split("T")[0];
    },
    // End:: Format Date

    // Start:: Get User Subscription History
    async getUserSubscriptionHistory() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: `subscriptions/show/${this.$route.params.userId}`,
        });

        const data = res.data.data.other_subscriptions;

        if (data && Array.isArray(data)) {
          this.tableRows = data.map((item) => ({
            id: item.id,
            user_id: item.user_id,
            start_date: item.start_date,
            end_date: item.end_date,
            price: item.price,
            tax_in_value: item.tax_in_value,
            status: item.status,
            type: item.type,
            deactivation_reason: item.deactivation_reason || "-",
            created_at: item.created_at,
            updated_at: item.updated_at,
            total_extensions_days: item.total_extensions_days || 0,
          }));
        } else {
          this.tableRows = [];
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Get User Subscription History
  },

  created() {
    this.getUserSubscriptionHistory();
  },
};
</script>

<style lang="scss" scoped>
.user_subscription_history {
  .table_title_wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .title_text_wrapper {
      h5 {
        color: #f7941e;
        font-size: 22px;
        font-weight: 600;
      }
    }
  }
}
</style>
